import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { publicPageAPI, filesAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Separator } from '../../components/ui/separator';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Globe, Eye, Save, Plus, Trash2, Upload, Image, Phone, Mail, MapPin,
  MessageCircle, Instagram, Link2, Check, X, ExternalLink, Loader2,
  GripVertical, Pencil, AlertCircle, Copy, CheckCircle2, Navigation, Send,
  Crown, Lock, Zap, ChevronLeft, ChevronRight, Search
} from 'lucide-react';
import { cn } from '../../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function MyPageEditor() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPro = user?.plan === 'pro';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [slugChecking, setSlugChecking] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const defaults = {
    slug: '',
    enabled: false,
    business_name: '',
    description: '',
    avatar_url: '',
    show_services: true,
    services: [],
    show_schedule: false,
    schedule: [
      { day: 'Понедельник', enabled: true, from: '09:00', to: '18:00' },
      { day: 'Вторник', enabled: true, from: '09:00', to: '18:00' },
      { day: 'Среда', enabled: true, from: '09:00', to: '18:00' },
      { day: 'Четверг', enabled: true, from: '09:00', to: '18:00' },
      { day: 'Пятница', enabled: true, from: '09:00', to: '18:00' },
      { day: 'Суббота', enabled: false, from: '10:00', to: '16:00' },
      { day: 'Воскресенье', enabled: false, from: '10:00', to: '16:00' },
    ],
    portfolio: [],
    contacts: { phone: '', email: '', telegram: '', instagram: '', vk: '', ok: '', address: '' },
    show_contacts: { phone: true, email: true, telegram: true, instagram: true, vk: true, ok: true, address: true },
    contact_button_type: 'telegram',
    contact_button_label: 'Связаться',
    show_seo: true,
    seo_title: '',
    seo_description: ''
  };

  const demoPreview = {
    ...defaults,
    slug: 'anna-torty',
    enabled: true,
    business_name: 'Мастерская Анны',
    description: 'Авторские торты на заказ. Работаю с натуральными ингредиентами, каждый торт — произведение искусства.',
    avatar_url: 'https://images.unsplash.com/photo-1581745069539-1e60d7f965f4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwzfHxlbGVnYW50JTIwZGVjb3JhdGVkJTIwY2FrZXxlbnwwfHx8fDE3NzMxNTg1ODN8MA&ixlib=rb-4.1.0&q=85',
    show_services: true,
    services: [
      { name: 'Торт на день рождения', price: 'от 3 500 ₽', description: 'Индивидуальный дизайн' },
      { name: 'Капкейки (набор 6 шт)', price: '1 800 ₽', description: 'Разные вкусы' },
      { name: 'Свадебный торт', price: 'от 8 000 ₽', description: 'Консультация бесплатно' },
    ],
    show_schedule: true,
    portfolio: [
      { url: 'https://images.pexels.com/photos/1359294/pexels-photo-1359294.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
      { url: 'https://images.pexels.com/photos/8882/love-heart-purple-dessert.jpg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
      { url: 'https://images.pexels.com/photos/7666502/pexels-photo-7666502.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
      { url: 'https://images.pexels.com/photos/1236680/pexels-photo-1236680.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
    ],
    contacts: { phone: '+7 (999) 123-45-67', email: 'anna@torty.ru', telegram: '@anna_torty', instagram: '@anna.torty', vk: '', ok: '', address: 'г. Москва' },
  };

  const [pageData, setPageData] = useState(defaults);

  const loadPage = useCallback(async () => {
    if (!isPro) {
      // Demo users see preview data
      setPageData(demoPreview);
      setLoading(false);
      return;
    }
    try {
      const res = await publicPageAPI.get();
      const data = res.data;
      // If schedule from server is empty, use default days
      if (!data.schedule || data.schedule.length === 0) {
        data.schedule = defaults.schedule;
      }
      setPageData(prev => ({ ...defaults, ...data }));
      if (res.data.slug) setSlugAvailable(true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [isPro]);

  useEffect(() => { loadPage(); }, [loadPage]);

  const updateField = (field, value) => {
    setPageData(prev => ({ ...prev, [field]: value }));
  };

  const updateContact = (field, value) => {
    setPageData(prev => ({
      ...prev,
      contacts: { ...prev.contacts, [field]: value }
    }));
  };

  const updateShowContact = (field, value) => {
    setPageData(prev => ({
      ...prev,
      show_contacts: { ...prev.show_contacts, [field]: value }
    }));
  };

  // Slug check with debounce
  useEffect(() => {
    if (!pageData.slug || pageData.slug.length < 3) {
      setSlugAvailable(null);
      return;
    }
    const timer = setTimeout(async () => {
      setSlugChecking(true);
      try {
        const res = await publicPageAPI.checkSlug(pageData.slug);
        setSlugAvailable(res.data.available);
      } catch {
        setSlugAvailable(null);
      } finally {
        setSlugChecking(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [pageData.slug]);

  const handleSave = async () => {
    if (!isPro) {
      toast.error('Функция «Моя страница» доступна только на тарифе Про');
      return;
    }
    if (!pageData.slug || pageData.slug.length < 3) {
      toast.error('Укажите адрес страницы (минимум 3 символа)');
      return;
    }
    setSaving(true);
    try {
      const res = await publicPageAPI.update(pageData);
      setPageData(res.data);
      toast.success('Страница сохранена');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Максимум 5 МБ'); return; }

    setUploadingAvatar(true);
    try {
      const res = await filesAPI.upload(file);
      updateField('avatar_url', res.data.file_path);
      toast.success('Аватар загружен');
    } catch {
      toast.error('Ошибка загрузки');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const currentCount = pageData.portfolio.length;
    const maxPhotos = 5;
    const remaining = maxPhotos - currentCount;
    if (remaining <= 0) {
      toast.error('Максимум 5 фотографий');
      e.target.value = '';
      return;
    }

    const filesToUpload = files.slice(0, remaining);
    if (files.length > remaining) {
      toast.warning(`Можно загрузить ещё ${remaining}. Лишние файлы пропущены.`);
    }

    setUploadingPhoto(true);
    try {
      const newPhotos = [];
      for (const file of filesToUpload) {
        if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name}: максимум 10 МБ`); continue; }
        const res = await filesAPI.upload(file);
        newPhotos.push({ url: res.data.file_path });
      }
      updateField('portfolio', [...pageData.portfolio, ...newPhotos]);
      toast.success(`Загружено ${newPhotos.length} фото`);
    } catch {
      toast.error('Ошибка загрузки');
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  const removePhoto = async (index) => {
    const photo = pageData.portfolio[index];
    const newPortfolio = pageData.portfolio.filter((_, i) => i !== index);
    updateField('portfolio', newPortfolio);
    try {
      await publicPageAPI.deletePhoto(photo.url);
    } catch { /* ignore */ }
  };

  // Services
  const addService = () => {
    updateField('services', [...pageData.services, { name: '', price: '', description: '' }]);
  };
  const updateService = (index, field, value) => {
    const newServices = [...pageData.services];
    newServices[index] = { ...newServices[index], [field]: value };
    updateField('services', newServices);
  };
  const removeService = (index) => {
    updateField('services', pageData.services.filter((_, i) => i !== index));
  };

  const updateScheduleDay = (index, field, value) => {
    const newSchedule = [...(pageData.schedule || [])];
    newSchedule[index] = { ...newSchedule[index], [field]: value };
    updateField('schedule', newSchedule);
  };

  const publicUrl = pageData.slug ? `${window.location.origin}/${pageData.slug}` : '';

  const copyLink = () => {
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    toast.success('Ссылка скопирована');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-4 pb-24">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-foreground">Моя страница</h1>
          <p className="text-sm text-muted-foreground">Публичная визитка вашего бизнеса</p>
        </div>
        <div className="flex gap-2">
          {publicUrl && pageData.enabled && isPro && (
            <Button variant="outline" size="sm" asChild>
              <a href={publicUrl} target="_blank" rel="noopener noreferrer">
                <Eye className="h-4 w-4 mr-1" />
                Посмотреть
              </a>
            </Button>
          )}
          {(!isPro || !(publicUrl && pageData.enabled)) && (
            <Button variant="outline" size="sm" onClick={() => setShowPreview(true)}>
              <Eye className="h-4 w-4 mr-1" />
              Посмотреть
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving || !isPro} size="sm">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            Сохранить
          </Button>
        </div>
      </div>

      {/* Demo Paywall Banner */}
      {!isPro && (
        <div className="mt-4 rounded-xl border-2 border-amber-500/30 bg-gradient-to-r from-amber-500/5 via-orange-500/5 to-amber-500/5 p-5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
              <Lock className="h-6 w-6 text-amber-500" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-base flex items-center gap-2">
                Моя страница — функция тарифа Про
                <Badge variant="outline" className="border-amber-500/30 text-amber-600 dark:text-amber-400 text-xs">
                  <Crown className="h-3 w-3 mr-1" />
                  Про
                </Badge>
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Ниже показан демо-вид редактора с примерными данными. Оформите подписку Про, чтобы создать свою публичную визитку, добавить услуги, портфолио и принимать клиентов через персональную ссылку.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 shrink-0">
              <Button
                variant="outline"
                onClick={() => setShowPreview(true)}
                className="border-amber-500/30 text-amber-600 hover:bg-amber-500/10"
              >
                <Eye className="h-4 w-4 mr-2" />
                Посмотреть пример
              </Button>
              <Button
                onClick={() => navigate('/pricing')}
                className="bg-amber-500 hover:bg-amber-600 text-white"
              >
                <Zap className="h-4 w-4 mr-2" />
                Оформить Про
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Status & URL */}
      <div className={cn(!isPro && "pointer-events-none select-none opacity-60")}>
      <div className="lg:grid lg:grid-cols-2 lg:gap-6 space-y-4 lg:space-y-0">

      {/* ===== LEFT COLUMN ===== */}
      <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Link2 className="h-4 w-4" />
            Адрес и статус
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Страница активна</Label>
              <p className="text-xs text-muted-foreground">Видна по ссылке всем</p>
            </div>
            <Switch checked={pageData.enabled} onCheckedChange={(v) => updateField('enabled', v)} />
          </div>
          <Separator />
          <div className="space-y-2">
            <Label className="text-sm font-medium">Адрес страницы</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground whitespace-nowrap hidden sm:inline">{window.location.origin}/</span>
              <span className="text-sm text-muted-foreground whitespace-nowrap sm:hidden">/</span>
              <div className="relative flex-1">
                <Input
                  value={pageData.slug}
                  onChange={(e) => updateField('slug', e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                  placeholder="my-business"
                  className={cn("pr-8", slugAvailable === false && "border-destructive")}
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  {slugChecking && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                  {!slugChecking && slugAvailable === true && <Check className="h-4 w-4 text-green-500" />}
                  {!slugChecking && slugAvailable === false && <X className="h-4 w-4 text-destructive" />}
                </div>
              </div>
            </div>
            {slugAvailable === false && (
              <p className="text-xs text-destructive">Этот адрес уже занят или зарезервирован</p>
            )}
          </div>
          {publicUrl && pageData.enabled && (
            <>
              <Separator />
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Ссылка на вашу страницу</Label>
                <p className="text-xs text-muted-foreground">Делитесь ей с клиентами в соцсетях и мессенджерах</p>
                <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
                  <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-sm truncate flex-1">{publicUrl}</span>
                  <Button variant="ghost" size="sm" className="h-7 px-2" onClick={copyLink}>
                    {copied ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 px-2" asChild>
                    <a href={publicUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Business Info */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">О бизнесе</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Avatar */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Логотип / аватар</Label>
            <div className="flex items-center gap-4">
              <div className="h-20 w-20 rounded-xl bg-muted flex items-center justify-center overflow-hidden border-2 border-dashed border-border">
                {pageData.avatar_url ? (
                  <img src={pageData.avatar_url.startsWith('http') ? pageData.avatar_url : `${BACKEND_URL}${pageData.avatar_url}`} alt="Avatar" className="h-full w-full object-cover" />
                ) : (
                  <Image className="h-8 w-8 text-muted-foreground" />
                )}
              </div>
              <div className="space-y-2">
                <label className="cursor-pointer">
                  <input type="file" className="hidden" accept="image/*" onChange={handleAvatarUpload} />
                  <Button variant="outline" size="sm" disabled={uploadingAvatar} asChild>
                    <span>
                      {uploadingAvatar ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                      Загрузить
                    </span>
                  </Button>
                </label>
                {pageData.avatar_url && (
                  <Button variant="ghost" size="sm" className="text-destructive" onClick={() => updateField('avatar_url', '')}>
                    <Trash2 className="h-3 w-3 mr-1" /> Удалить
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Название</Label>
            <Input value={pageData.business_name} onChange={(e) => updateField('business_name', e.target.value)} placeholder="Мастерская тортов" />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Описание</Label>
            <Textarea
              value={pageData.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="Расскажите о себе и своём бизнесе..."
              rows={4}
            />
          </div>
        </CardContent>
      </Card>

      {/* Services */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Switch checked={pageData.show_services !== false} onCheckedChange={(v) => updateField('show_services', v)} />
              <CardTitle className="text-base">Услуги</CardTitle>
            </div>
            {pageData.show_services !== false && (
              <Button variant="outline" size="sm" onClick={addService}>
                <Plus className="h-3.5 w-3.5 mr-1" /> Добавить
              </Button>
            )}
          </div>
          {pageData.show_services !== false && (
            <CardDescription>Список ваших услуг с ценами</CardDescription>
          )}
        </CardHeader>
        {pageData.show_services !== false && (
          <CardContent className="space-y-3">
          {pageData.services.length === 0 && (
            <div className="text-center py-6 text-muted-foreground text-sm">
              Нет услуг. Нажмите «Добавить» чтобы создать.
            </div>
          )}
          {pageData.services.map((service, i) => (
            <div key={i} className="p-3 rounded-lg border border-border space-y-2">
              <div className="flex items-start gap-2">
                <div className="flex-1 space-y-2">
                  <Input
                    value={service.name}
                    onChange={(e) => updateService(i, 'name', e.target.value)}
                    placeholder="Название услуги"
                    className="font-medium"
                  />
                  <div className="flex gap-2">
                    <Input
                      value={service.price}
                      onChange={(e) => updateService(i, 'price', e.target.value)}
                      placeholder="Цена (напр. от 2000 ₽)"
                      className="flex-1"
                    />
                  </div>
                  <Input
                    value={service.description || ''}
                    onChange={(e) => updateService(i, 'description', e.target.value)}
                    placeholder="Краткое описание (необязательно)"
                  />
                </div>
                <Button variant="ghost" size="sm" className="text-destructive h-8 w-8 p-0 shrink-0" onClick={() => removeService(i)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
          </CardContent>
        )}
      </Card>

      {/* Schedule */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Switch checked={pageData.show_schedule === true} onCheckedChange={(v) => updateField('show_schedule', v)} />
              <CardTitle className="text-base">График работы</CardTitle>
            </div>
          </div>
          {pageData.show_schedule === true && (
            <CardDescription>Укажите рабочие дни и часы</CardDescription>
          )}
        </CardHeader>
        {pageData.show_schedule === true && (
          <CardContent className="space-y-2">
            {(pageData.schedule || []).map((day, i) => (
              <div key={i} className="flex items-center gap-3">
                <Switch
                  checked={day.enabled}
                  onCheckedChange={(v) => updateScheduleDay(i, 'enabled', v)}
                  className="shrink-0"
                />
                <span className={cn(
                  "w-28 text-sm shrink-0",
                  !day.enabled && "text-muted-foreground"
                )}>
                  {day.day}
                </span>
                {day.enabled ? (
                  <div className="flex items-center gap-1.5">
                    <Input
                      type="time"
                      value={day.from}
                      onChange={(e) => updateScheduleDay(i, 'from', e.target.value)}
                      className="w-28 h-8 text-sm"
                    />
                    <span className="text-muted-foreground text-sm">—</span>
                    <Input
                      type="time"
                      value={day.to}
                      onChange={(e) => updateScheduleDay(i, 'to', e.target.value)}
                      className="w-28 h-8 text-sm"
                    />
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">Выходной</span>
                )}
              </div>
            ))}
          </CardContent>
        )}
      </Card>
      </div>{/* end LEFT COLUMN */}

      {/* ===== RIGHT COLUMN ===== */}
      <div className="space-y-4 mt-4 lg:mt-0">

      {/* Portfolio */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Портфолио</CardTitle>
              <CardDescription>{pageData.portfolio.length} из 5 фото</CardDescription>
            </div>
            {pageData.portfolio.length < 5 && (
              <label className="cursor-pointer">
                <input type="file" className="hidden" accept="image/*" multiple onChange={handlePhotoUpload} />
                <Button variant="outline" size="sm" disabled={uploadingPhoto} asChild>
                  <span>
                    {uploadingPhoto ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                    Загрузить
                  </span>
                </Button>
              </label>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {pageData.portfolio.length === 0 ? (
            <label className="cursor-pointer">
              <input type="file" className="hidden" accept="image/*" multiple onChange={handlePhotoUpload} />
              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors">
                <Image className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">Нажмите чтобы загрузить фото</p>
                <p className="text-xs text-muted-foreground mt-1">JPG, PNG до 10 МБ · Максимум 5 фото</p>
              </div>
            </label>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {pageData.portfolio.map((photo, i) => (
                <div key={i} className="group relative rounded-lg overflow-hidden border border-border">
                  <img
                    src={photo.url?.startsWith('http') ? photo.url : `${BACKEND_URL}${photo.url}`}
                    alt={`Фото ${i + 1}`}
                    className="w-full aspect-square object-cover"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <Button variant="destructive" size="sm" className="h-8" onClick={() => removePhoto(i)}>
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Удалить
                    </Button>
                  </div>
                </div>
              ))}
              {pageData.portfolio.length < 5 && (
                <label className="cursor-pointer">
                  <input type="file" className="hidden" accept="image/*" multiple onChange={handlePhotoUpload} />
                  <div className="border-2 border-dashed border-border rounded-lg aspect-square flex flex-col items-center justify-center hover:border-primary/50 transition-colors">
                    <Plus className="h-8 w-8 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground mt-1">Добавить</span>
                  </div>
                </label>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Contacts */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Контакты</CardTitle>
          <CardDescription>Какие контакты показывать на странице</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {[
            { key: 'phone', label: 'Телефон', placeholder: '+7 (999) 123-45-67' },
            { key: 'email', label: 'Email', placeholder: 'mail@example.com' },
            { key: 'telegram', label: 'Telegram', placeholder: '@username' },
            { key: 'instagram', label: 'Instagram', placeholder: '@username' },
            { key: 'vk', label: 'ВКонтакте', placeholder: 'id или username' },
            { key: 'ok', label: 'Одноклассники', placeholder: 'id или username' },
            { key: 'address', label: 'Адрес', placeholder: 'г. Москва, ул. ...' },
          ].map(({ key, label, placeholder }) => (
            <div key={key} className="flex items-center gap-3">
              <Switch
                checked={pageData.show_contacts?.[key] ?? true}
                onCheckedChange={(v) => updateShowContact(key, v)}
                className="shrink-0"
              />
              <span className="w-28 text-sm font-medium shrink-0">{label}</span>
              <Input
                value={pageData.contacts?.[key] || ''}
                onChange={(e) => updateContact(key, e.target.value)}
                placeholder={placeholder}
                className="flex-1"
              />
              {key === 'address' && pageData.contacts?.address && (
                <a
                  href={`https://yandex.ru/maps/?text=${encodeURIComponent(pageData.contacts.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Открыть на Яндекс Картах"
                >
                  <Button type="button" variant="outline" size="icon" className="h-9 w-9">
                    <Navigation className="h-4 w-4" />
                  </Button>
                </a>
              )}
            </div>
          ))}

          <Separator className="my-4" />

          <div className="space-y-2">
            <Label className="text-sm font-medium">Кнопка «Связаться»</Label>
            <div className="flex gap-2 flex-wrap">
              {['telegram', 'vk', 'phone', 'email'].map((type) => (
                <Button
                  key={type}
                  variant={pageData.contact_button_type === type ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => updateField('contact_button_type', type)}
                >
                  {type === 'telegram' && 'Telegram'}
                  {type === 'vk' && 'ВКонтакте'}
                  {type === 'phone' && 'Телефон'}
                  {type === 'email' && 'Email'}
                </Button>
              ))}
            </div>
            <Input
              value={pageData.contact_button_label}
              onChange={(e) => updateField('contact_button_label', e.target.value)}
              placeholder="Текст кнопки"
            />
          </div>
        </CardContent>
      </Card>

      {/* SEO Settings */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <Switch checked={pageData.show_seo !== false} onCheckedChange={(v) => updateField('show_seo', v)} />
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Search className="h-4 w-4" />
                SEO — продвижение в поиске
              </CardTitle>
              <CardDescription>Как ваша страница выглядит в Google и Яндекс</CardDescription>
            </div>
          </div>
        </CardHeader>
        {pageData.show_seo !== false && (
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Заголовок для поисковиков</Label>
            <Input
              value={pageData.seo_title || ''}
              onChange={(e) => updateField('seo_title', e.target.value)}
              placeholder={pageData.business_name || 'Название вашего бизнеса'}
              maxLength={70}
            />
            <p className="text-xs text-muted-foreground">{(pageData.seo_title || '').length}/70 символов. Если пусто — используется название бизнеса</p>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Описание для поисковиков</Label>
            <Textarea
              value={pageData.seo_description || ''}
              onChange={(e) => updateField('seo_description', e.target.value)}
              placeholder="Кратко опишите ваш бизнес и ключевые услуги для привлечения клиентов из поиска..."
              rows={3}
              maxLength={160}
              className="resize-y min-h-[60px]"
            />
            <p className="text-xs text-muted-foreground">{(pageData.seo_description || '').length}/160 символов. Если пусто — сформируется автоматически из описания и услуг</p>
          </div>

          {/* Google Preview */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Превью в поиске</Label>
            <div className="rounded-lg border border-border bg-white dark:bg-muted/30 p-4 space-y-1">
              <p className="text-sm text-blue-700 dark:text-blue-400 font-medium truncate">
                {pageData.seo_title || pageData.business_name || 'Название бизнеса'}
              </p>
              <p className="text-xs text-green-700 dark:text-green-500 truncate">
                {pageData.slug ? `${window.location.origin}/${pageData.slug}` : 'yadela.ru/ваш-адрес'}
              </p>
              <p className="text-xs text-muted-foreground line-clamp-2">
                {pageData.seo_description || pageData.description || 'Описание будет сформировано автоматически из информации о бизнесе и услугах'}
              </p>
            </div>
          </div>

          <div className="rounded-lg bg-muted/50 p-3 space-y-1">
            <p className="text-xs font-medium">Что уже работает автоматически:</p>
            <ul className="text-xs text-muted-foreground space-y-0.5 list-disc list-inside">
              <li>Структурированные данные (JSON-LD) для Google — название, услуги, контакты, график</li>
              <li>Open Graph теги — превью при отправке ссылки в соцсетях и мессенджерах</li>
              <li>Карточка Twitter — красивый превью в X/Twitter</li>
              <li>Sitemap — автоматическая индексация активных страниц</li>
            </ul>
          </div>
        </CardContent>
        )}
      </Card>
      </div>{/* end RIGHT COLUMN */}

      </div>{/* end grid */}
      </div>{/* end demo wrapper */}

      {/* Floating save button */}
      <div className="fixed bottom-6 right-6 z-50">
        <Button onClick={handleSave} disabled={saving || !isPro} size="lg" className={cn("shadow-lg", !isPro && "opacity-50")}>
          {saving ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Save className="h-5 w-5 mr-2" />}
          {isPro ? 'Сохранить' : 'Только для Про'}
        </Button>
      </div>

      {/* Page Preview Overlay */}
      {showPreview && (
        <PagePreviewOverlay pageData={pageData} onClose={() => setShowPreview(false)} isPro={isPro} onUpgrade={() => { setShowPreview(false); navigate('/pricing'); }} />
      )}
    </div>
  );
}


/* ─── Helper functions for preview ─── */
function getContactLink(type, value) {
  if (!value) return null;
  switch (type) {
    case 'telegram': return value.startsWith('http') ? value : `https://t.me/${value.replace('@', '')}`;
    case 'phone': return `tel:${value}`;
    case 'email': return `mailto:${value}`;
    case 'instagram': return value.startsWith('http') ? value : `https://instagram.com/${value.replace('@', '')}`;
    case 'vk': return value.startsWith('http') ? value : `https://vk.com/${value}`;
    case 'ok': return value.startsWith('http') ? value : `https://ok.ru/profile/${value}`;
    default: return null;
  }
}

const previewContactLabels = { phone: 'Телефон', email: 'Email', telegram: 'Telegram', instagram: 'Instagram', vk: 'ВКонтакте', ok: 'Одноклассники', address: 'Адрес' };

/* ─── Full-screen Page Preview Overlay ─── */
function PagePreviewOverlay({ pageData, onClose, isPro, onUpgrade }) {
  const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose]);

  const contactOrder = ['phone', 'address', 'email', 'telegram', 'instagram', 'vk', 'ok'];
  const contactEntries = Object.entries(pageData.contacts || {}).filter(([_, v]) => v).sort((a, b) => {
    const ia = contactOrder.indexOf(a[0]);
    const ib = contactOrder.indexOf(b[0]);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });
  const ctaLink = getContactLink(pageData.contact_button_type, pageData.contacts?.[pageData.contact_button_type]);
  const services = pageData.services || [];
  const schedule = pageData.schedule || [];
  const showSchedule = pageData.show_schedule && schedule.length > 0;
  const portfolioPhotos = pageData.portfolio || [];
  const addressValue = pageData.contacts?.address;
  const mapsLink = addressValue ? `https://yandex.ru/maps/?text=${encodeURIComponent(addressValue)}` : null;
  const hasServices = services.length > 0;
  const hasSchedule = showSchedule;
  const hasPortfolio = portfolioPhotos.length > 0;
  const hasContacts = contactEntries.length > 0;
  const hasContent = hasServices || hasSchedule || hasPortfolio || hasContacts;

  const ContactIcon = ({ type, className }) => {
    const icons = { phone: Phone, email: Mail, address: MapPin };
    const Icon = icons[type] || Globe;
    return <Icon className={className} />;
  };

  return (
    <div className="fixed inset-0 z-[90] flex flex-col">
      {/* Top Bar */}
      <div className="bg-white dark:bg-stone-900 border-b shadow-sm px-3 sm:px-4 py-2 sm:py-2.5 flex items-center justify-between shrink-0 z-10 gap-2">
        <div className="flex items-center gap-1.5 sm:gap-3 min-w-0">
          <Eye className="h-4 w-4 sm:h-5 sm:w-5 text-orange-500 shrink-0" />
          <span className="font-semibold text-xs sm:text-sm truncate">Предпросмотр</span>
          {!isPro && (
            <span className="text-[10px] sm:text-xs bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 px-1.5 sm:px-2 py-0.5 rounded-full font-medium whitespace-nowrap shrink-0">
              Демо
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          {!isPro && (
            <Button size="sm" onClick={onUpgrade} className="bg-amber-500 hover:bg-amber-600 text-white h-7 sm:h-8 text-[10px] sm:text-xs px-2 sm:px-3">
              <Zap className="h-3 w-3 sm:h-3.5 sm:w-3.5 sm:mr-1" />
              <span className="hidden sm:inline">Оформить Про</span>
              <span className="sm:hidden">Про</span>
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={onClose} className="h-7 sm:h-8 text-[10px] sm:text-xs px-2 sm:px-3">
            <X className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
          </Button>
        </div>
      </div>

      {/* Preview Content — scrollable, renders like PublicPage */}
      <div className="flex-1 overflow-y-auto bg-stone-100">
        {/* Hero */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-amber-50/50 to-orange-50/30" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(251,146,60,0.08),transparent_50%)]" />
          <div className="relative max-w-3xl mx-auto px-5 pt-16 pb-14 lg:pt-20 lg:pb-16 text-center">
            {pageData.avatar_url && (
              <div className="mb-7">
                <img src={pageData.avatar_url.startsWith('http') ? pageData.avatar_url : `${BACKEND}${pageData.avatar_url}`} alt={pageData.business_name} className="h-28 w-28 lg:h-36 lg:w-36 rounded-3xl object-cover mx-auto shadow-xl ring-4 ring-orange-100" />
              </div>
            )}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-stone-900">
              {pageData.business_name || 'Название бизнеса'}
            </h1>
            {pageData.description && (
              <p className="mt-5 text-stone-500 text-base sm:text-lg lg:text-xl leading-relaxed max-w-lg mx-auto whitespace-pre-line">{pageData.description}</p>
            )}
            {(ctaLink || mapsLink) && (
              <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
                {ctaLink && (
                  <span className="inline-flex items-center gap-2.5 bg-orange-500 text-white font-semibold px-8 py-3.5 lg:px-10 lg:py-4 rounded-full text-base lg:text-lg shadow-lg shadow-orange-500/20 cursor-default">
                    <Send className="h-4 w-4 lg:h-5 lg:w-5" />
                    {pageData.contact_button_label || 'Связаться'}
                  </span>
                )}
                {mapsLink && (
                  <span className="inline-flex items-center gap-2.5 bg-stone-800 text-white font-semibold px-8 py-3.5 lg:px-10 lg:py-4 rounded-full text-base lg:text-lg shadow-lg shadow-stone-800/20 cursor-default">
                    <Navigation className="h-4 w-4 lg:h-5 lg:w-5" />
                    В путь
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        {hasContent && (
          <div className="max-w-5xl mx-auto px-5 pt-6 lg:pt-14 pb-8">
            <div className="lg:grid lg:grid-cols-2 lg:gap-12">

              {/* Left: Services + Schedule */}
              <div>
                {hasServices && (
                  <section className="mb-10">
                    <h2 className="text-lg font-bold text-stone-900 mb-4 flex items-center gap-3">
                      <span className="h-9 w-9 rounded-xl bg-orange-50 flex items-center justify-center shrink-0">
                        <ExternalLink className="h-4 w-4 text-orange-500" />
                      </span>
                      Услуги
                    </h2>
                    <div className="flex flex-col gap-3.5">
                      {services.map((s, i) => (
                        <div key={i} className="flex items-center justify-between p-4 lg:p-5 rounded-2xl bg-white border border-stone-100 shadow-sm">
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-stone-800">{s.name}</div>
                            {s.description && <p className="text-sm text-stone-400 mt-0.5 leading-snug">{s.description}</p>}
                          </div>
                          {s.price && <span className="ml-4 shrink-0 text-sm font-bold text-orange-500 bg-orange-50 px-3 py-1.5 rounded-xl">{s.price}</span>}
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {hasSchedule && (
                  <section className="mb-10">
                    <h2 className="text-lg font-bold text-stone-900 mb-4 flex items-center gap-3">
                      <span className="h-9 w-9 rounded-xl bg-orange-50 flex items-center justify-center shrink-0">
                        <Globe className="h-4 w-4 text-orange-500" />
                      </span>
                      График работы
                    </h2>
                    <div className="rounded-2xl bg-white border border-stone-100 shadow-sm overflow-hidden">
                      {schedule.map((day, i) => (
                        <div key={i} className={`flex items-center justify-between px-5 py-3.5 ${i < schedule.length - 1 ? 'border-b border-stone-50' : ''}`}>
                          <span className={`text-sm font-medium ${day.enabled ? 'text-stone-700' : 'text-stone-300'}`}>{day.day}</span>
                          {day.enabled
                            ? <span className="text-sm text-stone-500 font-mono">{day.from} — {day.to}</span>
                            : <span className="text-xs text-stone-300 italic">Выходной</span>
                          }
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </div>

              {/* Right: Portfolio + Contacts */}
              <div className={`${(hasServices || hasSchedule) ? 'mt-10 lg:mt-0' : ''}`}>
                {hasPortfolio && (
                  <section className="mb-10">
                    <h2 className="text-lg font-bold text-stone-900 mb-4 flex items-center gap-3">
                      <span className="h-9 w-9 rounded-xl bg-orange-50 flex items-center justify-center shrink-0">
                        <Image className="h-4 w-4 text-orange-500" />
                      </span>
                      Портфолио
                    </h2>
                    <PreviewCarousel photos={portfolioPhotos} backendUrl={BACKEND} />
                  </section>
                )}

                {hasContacts && (
                  <section className="mb-10">
                    <h2 className="text-lg font-bold text-stone-900 mb-4 flex items-center gap-3">
                      <span className="h-9 w-9 rounded-xl bg-orange-50 flex items-center justify-center shrink-0">
                        <Phone className="h-4 w-4 text-orange-500" />
                      </span>
                      Контакты
                    </h2>
                    <div className="flex flex-col gap-3">
                      {contactEntries.map(([key, value]) => (
                        <div key={key} className="flex items-center gap-3.5 p-4 rounded-2xl bg-white border border-stone-100 shadow-sm">
                          <div className="h-10 w-10 rounded-xl bg-orange-50 flex items-center justify-center shrink-0">
                            <ContactIcon type={key} className="h-4 w-4 text-orange-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[11px] text-stone-400 uppercase tracking-wider font-medium">{previewContactLabels[key] || key}</div>
                            <div className="text-sm font-semibold text-stone-700 break-words">{value}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </div>

            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center pt-2 pb-8 text-xs text-stone-400">
          Создано на <span className="text-orange-500 font-medium">Yadela</span>
        </div>
      </div>
    </div>
  );
}


/* ─── Portfolio Carousel for Preview ─── */
function PreviewCarousel({ photos, backendUrl }) {
  const [current, setCurrent] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const [touchStart, setTouchStart] = useState(null);
  const autoRef = useRef(null);
  const total = photos.length;

  const go = useCallback((idx) => { setCurrent(((idx % total) + total) % total); }, [total]);
  const next = useCallback(() => go(current + 1), [current, go]);
  const prev = useCallback(() => go(current - 1), [current, go]);

  useEffect(() => { autoRef.current = setInterval(next, 4500); return () => clearInterval(autoRef.current); }, [next]);
  const pauseAuto = () => clearInterval(autoRef.current);
  const resumeAuto = () => { clearInterval(autoRef.current); autoRef.current = setInterval(next, 4500); };

  const onTouchStart = (e) => { setTouchStart(e.touches[0].clientX); pauseAuto(); };
  const onTouchEnd = (e) => {
    if (touchStart === null) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) { diff > 0 ? next() : prev(); }
    setTouchStart(null);
    resumeAuto();
  };

  useEffect(() => {
    if (lightbox === null) return;
    const handle = (e) => {
      if (e.key === 'ArrowRight') setLightbox(p => ((p + 1) % total + total) % total);
      if (e.key === 'ArrowLeft') setLightbox(p => ((p - 1) % total + total) % total);
      if (e.key === 'Escape') setLightbox(null);
    };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [lightbox, total]);

  const imgSrc = (photo) => photo.url?.startsWith('http') ? photo.url : `${backendUrl}${photo.url}`;

  if (total === 0) return null;

  return (
    <>
      <div className="relative overflow-hidden rounded-2xl group shadow-sm" onMouseEnter={pauseAuto} onMouseLeave={resumeAuto} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <div className="flex transition-transform duration-500 ease-out" style={{ transform: `translateX(-${current * 100}%)` }}>
          {photos.map((photo, i) => (
            <div key={i} className="w-full shrink-0">
              <div className="relative aspect-[4/3] cursor-pointer" onClick={() => setLightbox(i)}>
                <img src={imgSrc(photo)} alt={`Работа ${i + 1}`} className="w-full h-full object-cover" draggable={false} />
              </div>
            </div>
          ))}
        </div>
        {total > 1 && (
          <>
            <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white">
              <ChevronLeft className="h-5 w-5 text-stone-700" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white">
              <ChevronRight className="h-5 w-5 text-stone-700" />
            </button>
          </>
        )}
        {total > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {photos.map((_, i) => (
              <button key={i} onClick={() => go(i)} className={`h-2 rounded-full transition-all duration-300 ${i === current ? 'w-6 bg-orange-500 shadow-md' : 'w-2 bg-white/50 hover:bg-white/80'}`} />
            ))}
          </div>
        )}
      </div>
      {lightbox !== null && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center" onClick={() => setLightbox(null)}>
          <button className="absolute top-4 right-4 text-white/80 hover:text-white z-10" onClick={() => setLightbox(null)}><X className="h-6 w-6" /></button>
          {total > 1 && (
            <>
              <button className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white z-10 p-2" onClick={(e) => { e.stopPropagation(); setLightbox(p => ((p - 1) % total + total) % total); }}><ChevronLeft className="h-8 w-8" /></button>
              <button className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white z-10 p-2" onClick={(e) => { e.stopPropagation(); setLightbox(p => ((p + 1) % total + total) % total); }}><ChevronRight className="h-8 w-8" /></button>
            </>
          )}
          <div className="max-w-4xl max-h-[85vh] px-4" onClick={(e) => e.stopPropagation()}>
            <img src={imgSrc(photos[lightbox])} alt="" className="max-w-full max-h-[80vh] object-contain rounded-lg" />
            <p className="text-white/50 text-center mt-2 text-xs">{lightbox + 1} / {total}</p>
          </div>
        </div>
      )}
    </>
  );
}
